import { useState, useEffect } from 'react';
import {
  getRegistrationSubmitters,
  getRegistrationOriginators,
  getRegistrationStatusList,
} from '../../../api/epoApplicationsFilters';
import FilterField from '../../LoadFilters/FilterField';
import { hasAnyFilterSelected, getFilterMinLengthError, hasFilterMinLengthErrors } from '../../LoadFilters/utils';
import PropTypes from 'prop-types';

const TYPE_TO_SEARCH_DELAY = 3000;

const styles = {
  fields: 'd-flex gap-4 justify-content-between',
  column: 'd-flex flex-column gap-2 flex-grow-1',
  dateField: 'd-flex gap-2',
};

const initialDropdownOptions = {
  statuses: [],
  originators: [],
  submitters: [],
  isLoaded: false,
};

const EpoApplicationsFilters = ({ visible, values, onChange, onSubmit }) => {
  const [dropdownOptions, setDropdownOptions] = useState(initialDropdownOptions);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const loadStatusAndOriginator = () => {
    if (dropdownOptions.isLoaded) return;
    Promise.all([getRegistrationStatusList(), getRegistrationOriginators()]).then(([statusRes, originatorRes]) => {
      const statusList = Array.isArray(statusRes) ? statusRes : (statusRes?.data ?? statusRes?.results ?? []);
      const statuses = statusList.filter((s) => s.status != null).map((s) => ({ id: s.status, data: s.status }));
      const originatorList = Array.isArray(originatorRes)
        ? originatorRes
        : (originatorRes?.data ?? originatorRes?.results ?? []);
      const originators = originatorList
        .filter((o) => o.status != null)
        .map((o) => ({ id: String(o.status), data: String(o.status) }));
      setDropdownOptions((prev) => ({
        ...prev,
        statuses,
        originators,
        isLoaded: true,
      }));
    });
  };

  useEffect(() => () => clearTimeout(searchTimeout), [searchTimeout]);

  // Clear submitter dropdown options when the submitter selection is cleared
  useEffect(() => {
    if (!values?.submitter) {
      setDropdownOptions((prev) => ({ ...prev, submitters: [] }));
    }
  }, [values?.submitter]);

  const handleChange = (e) => {
    onChange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmitterSearch = (searchTerm) => {
    clearTimeout(searchTimeout);
    if (!searchTerm || searchTerm.trim().length === 0) {
      setIsSearchLoading(false);
      setDropdownOptions((prev) => ({ ...prev, submitters: [] }));
      return;
    }
    setIsSearchLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const data = await getRegistrationSubmitters(searchTerm.trim());
        const arr = Array.isArray(data) ? data : [];
        const submitters = arr.filter((s) => s.data != null).map((s) => ({ id: s.data, data: s.data }));
        setDropdownOptions((prev) => ({ ...prev, submitters }));
      } catch (err) {
        console.error(err);
        setDropdownOptions((prev) => ({ ...prev, submitters: [] }));
      }
      setIsSearchLoading(false);
    }, TYPE_TO_SEARCH_DELAY);
    setSearchTimeout(timeout);
  };

  if (!visible) return null;

  const applyDisabled = !hasAnyFilterSelected(values) || hasFilterMinLengthErrors(values);

  return (
    <div className="px-3 mt-4">
      <div className={styles.fields}>
        <div className={styles.column}>
          <FilterField label="REFERENCE" field="reference" value={values.reference} onChange={handleChange} />
          <FilterField
            label="COMPANY NAME"
            field="companyName"
            value={values.companyName}
            onChange={handleChange}
            error={getFilterMinLengthError('companyName', values.companyName)}
          />
          <FilterField
            label="EORI NUMBER"
            field="eoriNumber"
            value={values.eoriNumber}
            onChange={handleChange}
            error={getFilterMinLengthError('eoriNumber', values.eoriNumber)}
          />
        </div>
        <div className={styles.column}>
          <FilterField
            label="STATUS"
            field="status"
            value={values.status}
            options={dropdownOptions.statuses}
            onChange={handleChange}
            onDropdownOpen={loadStatusAndOriginator}
          />
          <FilterField
            label="ORIGINATOR"
            field="originator"
            value={values.originator}
            options={dropdownOptions.originators}
            onChange={handleChange}
            onDropdownOpen={loadStatusAndOriginator}
          />
          <FilterField
            label="SUBMITTER NAME"
            field="submitter"
            value={values.submitter}
            options={dropdownOptions.submitters}
            onChange={handleChange}
            onSearchChange={(searchTerm) => handleSubmitterSearch(searchTerm)}
            isLoading={isSearchLoading}
          />
        </div>
        <div className={styles.column}>
          <div className={styles.dateField}>
            <FilterField
              type="date"
              label="LAST UPDATED"
              field="lastModifiedFrom"
              value={values.lastModifiedFrom}
              onChange={handleChange}
              max={values.lastModifiedTo || undefined}
            />
            <FilterField
              type="date"
              field="lastModifiedTo"
              value={values.lastModifiedTo}
              onChange={handleChange}
              min={values.lastModifiedFrom || undefined}
            />
          </div>
          <div className={styles.dateField}>
            <FilterField
              type="date"
              label="CREATED DATE"
              field="createdDateFrom"
              value={values.createdDateFrom}
              onChange={handleChange}
              max={values.createdDateTo || undefined}
            />
            <FilterField
              type="date"
              field="createdDateTo"
              value={values.createdDateTo}
              onChange={handleChange}
              min={values.createdDateFrom || undefined}
            />
          </div>
        </div>
      </div>
      <div className="text-end">
        <button className="stepper-button yellow" onClick={onSubmit} disabled={applyDisabled}>
          Apply Filter
        </button>
      </div>
    </div>
  );
};

EpoApplicationsFilters.propTypes = {
  visible: PropTypes.bool,
  values: PropTypes.shape({
    reference: PropTypes.string,
    companyName: PropTypes.string,
    eoriNumber: PropTypes.string,
    status: PropTypes.string,
    originator: PropTypes.string,
    submitter: PropTypes.string,
    lastModifiedFrom: PropTypes.string,
    lastModifiedTo: PropTypes.string,
    createdDateFrom: PropTypes.string,
    createdDateTo: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EpoApplicationsFilters;
